import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import Logo from "../../Assets/Images/Logo.svg";
import "./index.scss";
import { useAuth } from "../../context/AuthContext";
import { useCompanySelection } from "../../context/CompanySelectionContext";
import { requireDashboardUrl } from "../../lib/utils";

const DASHBOARD_BASE = requireDashboardUrl();

const CompanySwitcher = () => {
  const { companies, selectedCompany, selectCompany } = useCompanySelection();

  if (!companies || companies.length === 0) {
    return null;
  }

  if (companies.length === 1) {
    return (
      <span className="company-badge" title={companies[0].name}>
        {companies[0].name}
      </span>
    );
  }

  return (
    <Autocomplete
      className="company-switcher"
      size="small"
      disableClearable
      options={companies}
      value={selectedCompany}
      getOptionLabel={(option) => option?.name || ""}
      isOptionEqualToValue={(option, value) => option.companyId === value?.companyId}
      onChange={(_, value) => value && selectCompany(value.companyId)}
      renderInput={(params) => (
        <TextField {...params} placeholder="Select company" />
      )}
    />
  );
};

const Header = () => {
  const navigate = useNavigate();
  const { user, signOut, loading } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleLogoClick = () => navigate("/");

  const handleSupportClick = () => {
    window.open("mailto:support@canaryai.com", "_blank", "noopener,noreferrer");
  };

  const handleGoToDashboard = () => {
    const loginHint = user?.username || localStorage.getItem("lastUpn");
    const base = `${DASHBOARD_BASE}/dashboard`;
    const url = loginHint
      ? `${base}?login_hint=${encodeURIComponent(loginHint)}`
      : base;
    window.location.href = url;
  };

  const handleLogout = () => {
    setMenuOpen(false);
    signOut();
    navigate("/");
  };

  const toggleMenu = () => {
    setMenuOpen((prev) => !prev);
  };

  const username = user?.name || "Guest";
  const email = user?.username || "";
  const initials = username
    .split(" ")
    .map((part) => part[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  return (
    <header className="app-header">
      <div className="header-left">
        <img
          src={Logo}
          alt="Canary logo"
          className="header-logo"
          onClick={handleLogoClick}
        />
        <CompanySwitcher />
      </div>

      <div className="header-right">
        <button
          type="button"
          className="dashboard-chip"
          onClick={handleGoToDashboard}
        >
          Go to Dashboard
        </button>

        <button
          type="button"
          className="support-chip"
          onClick={handleSupportClick}
        >
          <MailOutlineIcon className="support-icon" />
          <span>Support</span>
        </button>

        <div className="user-chip" ref={dropdownRef}>
          <button
            type="button"
            className="user-pill"
            onClick={toggleMenu}
            aria-haspopup="true"
            aria-expanded={menuOpen}
          >
            <span className="avatar-badge">{initials}</span>
            <span className="user-name">{username}</span>
            <MoreVertIcon className="menu-icon" />
          </button>

          {menuOpen && (
            <div className="chip-menu">
              <div className="chip-menu-header">
                <p className="chip-menu-name">{username}</p>
                {email && <p className="chip-menu-email">{email}</p>}
              </div>
              <div className="chip-menu-divider" />
              <button
                type="button"
                className="chip-menu-item chip-menu-logout"
                onClick={handleLogout}
              >
                {loading ? "Please wait..." : "Logout"}
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;
